import AddIcon from "@mui/icons-material/AddRounded";
import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMoveOutlined";
import EditIcon from "@mui/icons-material/EditOutlined";
import DownloadIcon from "@mui/icons-material/SaveAltRounded";
import { Button, Divider, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import { useState } from "react";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import FileAccessIcon from "@entrilia/shared/icons/FileAccessIcon";
import CategoryTag from "../../../../../common/CategoryTag";
import useFundraisingDetailsPageContext from "../useFundraisingDetailsPageContext";
import EditFileSectionDialog from "./EditFileSectionDialog";
import FundraisingDocumentsContext from "./FundraisingDocumentsContext";
import {
  deleteDocumentsAction,
  isChangeAccessOptionsAllowed,
  selectAccessOptions,
  updateDocumentsAccessOptionsAction,
  updateDocumentsCategoryAction,
  updateDocumentsSectionAction,
} from "./fundraisingDocumentsState";

interface Props {
  selectedIds: string[];
  disabled?: boolean;
}

const FundraisingDocumentsGridToolbar = ({ selectedIds, disabled }: Props) => {
  const { fundraisingCategories, isContentEditable } = useFundraisingDetailsPageContext();
  const { documentsState, setDocumentsState, onDownloadFile, onDownloadMultipleFiles } =
    useDefinedContext(FundraisingDocumentsContext);

  const [showNewSectionDialog, setShowNewSectionDialog] = useState(false);

  const [isSectionSelectorOpen, setIsSectionSelectorOpen] = useState(false);
  const [changeSectionButtonEl, setChangeSectionButtonEl] = useState<HTMLButtonElement | null>(null);

  const [isCategorySelectorOpen, setIsCategorySelectorOpen] = useState(false);
  const [changeCategoryButtonEl, setChangeCategoryButtonEl] = useState<HTMLButtonElement | null>(null);

  const [isFileAccessSelectorOpen, setIsFileAccessSelectorOpen] = useState(false);
  const [changeFileAccessButtonEl, setChangeFileAccessButtonEl] = useState<HTMLButtonElement | null>(null);

  const handleDownloadClick = () => {
    if (selectedIds.length === 1) {
      onDownloadFile(defined(selectedIds[0]));
    } else {
      onDownloadMultipleFiles(selectedIds);
    }
  };

  const handleCreateNewSection = () => {
    setIsSectionSelectorOpen(false);
    setShowNewSectionDialog(true);
  };

  const handleNewSectionCreated = (section: string) => {
    setShowNewSectionDialog(false);
    setDocumentsState(updateDocumentsSectionAction(selectedIds, section));
  };

  const selectedUploadingDocuments = documentsState.documents.filter(
    (doc) => selectedIds.includes(doc.fileId) && doc.uploadStatus === "uploading"
  );

  const selectedDocumentsWithChangeableAccess = documentsState.documents.filter(
    (doc) => selectedIds.includes(doc.fileId) && isChangeAccessOptionsAllowed(doc)
  );

  const menuSlotProps = {
    paper: { sx: { minWidth: "12rem" } },
  };

  return (
    <>
      {isContentEditable && (
        <Button
          ref={(el) => setChangeSectionButtonEl(el)}
          disabled={disabled}
          onClick={() => setIsSectionSelectorOpen(true)}
          color="secondary"
          variant="text"
          startIcon={<DriveFileMoveIcon />}
        >
          Change Section
        </Button>
      )}

      {isContentEditable && (
        <Button
          ref={(el) => setChangeCategoryButtonEl(el)}
          disabled={disabled}
          onClick={() => setIsCategorySelectorOpen(true)}
          color="secondary"
          variant="text"
          startIcon={<EditIcon />}
        >
          Change Category
        </Button>
      )}

      {isContentEditable && (
        <Button
          ref={(el) => setChangeFileAccessButtonEl(el)}
          disabled={disabled || selectedDocumentsWithChangeableAccess.length === 0}
          onClick={() => setIsFileAccessSelectorOpen(true)}
          color="secondary"
          variant="text"
          startIcon={<FileAccessIcon />}
        >
          File Access
        </Button>
      )}

      <Button
        disabled={disabled || selectedUploadingDocuments.length > 0}
        onClick={handleDownloadClick}
        color="secondary"
        variant="text"
        startIcon={<DownloadIcon />}
      >
        Download
      </Button>

      {isContentEditable && (
        <Button
          disabled={disabled}
          onClick={() => setDocumentsState(deleteDocumentsAction(selectedIds))}
          color="error"
          variant="text"
          startIcon={<DeleteIcon />}
        >
          Delete
        </Button>
      )}

      <Menu
        open={isSectionSelectorOpen}
        onClose={() => setIsSectionSelectorOpen(false)}
        anchorEl={changeSectionButtonEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={menuSlotProps}
      >
        {documentsState.orderedSections.map((section) => (
          <MenuItem
            key={section}
            value={section}
            onClick={() => setDocumentsState(updateDocumentsSectionAction(selectedIds, section))}
          >
            {section}
          </MenuItem>
        ))}

        <Divider />

        <MenuItem onClick={() => handleCreateNewSection()}>
          <ListItemIcon>
            <AddIcon color="primary" />
          </ListItemIcon>
          <ListItemText slotProps={{ primary: { color: "primary" } }}>Create New</ListItemText>
        </MenuItem>
      </Menu>

      <Menu
        open={isCategorySelectorOpen}
        onClose={() => setIsCategorySelectorOpen(false)}
        anchorEl={changeCategoryButtonEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={menuSlotProps}
      >
        {fundraisingCategories.map((category) => (
          <MenuItem
            key={category.externalId}
            value={category.externalId}
            dense
            onClick={() => setDocumentsState(updateDocumentsCategoryAction(selectedIds, category.externalId))}
          >
            <CategoryTag externalCategoryId={category.externalId} />
          </MenuItem>
        ))}
      </Menu>

      <Menu
        open={isFileAccessSelectorOpen}
        onClose={() => setIsFileAccessSelectorOpen(false)}
        anchorEl={changeFileAccessButtonEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={menuSlotProps}
      >
        {selectAccessOptions.map(([value, options]) => (
          <MenuItem
            key={value}
            value={value}
            onClick={() =>
              setDocumentsState(
                updateDocumentsAccessOptionsAction(
                  selectedDocumentsWithChangeableAccess.map((d) => d.fileId),
                  options
                )
              )
            }
          >
            {value}
          </MenuItem>
        ))}
      </Menu>

      {showNewSectionDialog && (
        <EditFileSectionDialog onClose={() => setShowNewSectionDialog(false)} onCreated={handleNewSectionCreated} />
      )}
    </>
  );
};

export default FundraisingDocumentsGridToolbar;
